import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CommonModule, Location } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ControllerSettings } from '../../model/controller-settings';
import { WebsocketService } from '../../services/websocket.service';
import { AnimationService } from '../../services/animation.service';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [TranslatePipe, CommonModule, FormsModule, RouterModule],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss'
})
export class SettingsComponent implements OnInit {
  availableMasters: any[] = [];
  formError: string = '';
  /** Interpolation values for formError, e.g. { field: 'WiFi SSID' }. */
  formErrorParams: Record<string, string> = {};
  mac: string = '';
  settings: ControllerSettings | null = null;  
  customParamsEntries: Array<{ key: string; value: string }> = [];
  // Per-relay hold PWM (RCV2* only). { output id -> pwm }, 0/absent = board default (~2000).
  relayHoldEntries: Array<{ output: number; pwm: number }> = [];
  controllerModel = '';
  outputsCount = 0;

  constructor(private route: ActivatedRoute,
              private router: Router,
              private location: Location,
              private animationService: AnimationService,
              private websocketService: WebsocketService) {}

  ngOnInit() {
    this.mac = this.route.snapshot.paramMap.get('mac') || '';    
    this.websocketService.getUserDevices().subscribe((devices) => {
      const dev = devices?.find((c: any) => c.mac === this.mac);
      this.controllerModel = dev?.model ?? '';
      this.outputsCount = dev?.config?.outputs?.length ?? 0;
      if (devices != null) {
        this.settings = devices.find((ctrl: { mac: string; }) => ctrl.mac === this.mac).settings;        
        // fix config for unknown properties
        if (!this.settings) {
          this.formError = 'settings.err.no_config';
        }
        console.log('settings load',this.settings);
        if (this.settings) {
          if (!this.settings?.eth) {
            this.settings!.eth = { 
              enabled: true, 
              dhcp: true, 
              ip: '0.0.0.0', 
              mask: '0.0.0.0', 
              gw: '0.0.0.0', 
              dns: '0.0.0.0', 
              enableReset: false, 
              resetGpio: -1 
            };
          }
          if (!this.settings?.wifi) {
            this.settings!.wifi = { 
              enabled: false, 
              dhcp: true, 
              ip: '0.0.0.0', 
              mask: '0.0.0.0', 
              gw: '0.0.0.0', 
              ssid: '', 
              pass: '', 
              dns: '0.0.0.0' 
            };
          }
          if (!this.settings?.cloud) {
            this.settings!.cloud = { enabled: true, address: 'wss://rss.akpeisov.kz/ws' };
          }
          if (!this.settings?.mqtt) {
            this.settings!.mqtt = { enabled: false, address: '' };
          }
          if (!this.settings?.ota) {
            this.settings!.ota = { address: 'https://fw.akpeisov.kz/rc/RelayController.bin' };
          }
          if (!this.settings?.ntp) {
            this.settings!.ntp = { address: 'pool.ntp.org', tz: 'UTC-5:00' };
          }
          if (!this.settings?.theme) {
            this.settings!.theme = 'standard';
          }
          this.parseRelayHold();
        }      
      }
    });
    
  }

  get isRCV2(): boolean {
    return this.controllerModel?.startsWith('RCV2') ?? false;
  }

  private parseRelayHold() {
    this.relayHoldEntries = [];
    const raw: any = this.settings?.relayHold;
    if (raw && typeof raw === 'object') {
      for (const k of Object.keys(raw))
        this.relayHoldEntries.push({ output: Number(k), pwm: Number(raw[k]) });
      this.relayHoldEntries.sort((a, b) => a.output - b.output);
    }
  }

  addRelayHold() {
    this.relayHoldEntries.push({ output: 0, pwm: 2000 });
  }

  removeRelayHold(i: number) {
    this.relayHoldEntries.splice(i, 1);
  }

  outputRange(): number[] {
    return Array.from({ length: this.outputsCount }, (_, i) => i);
  }

  private serializeRelayHold(): void {
    if (!this.settings) return;
    const obj: any = {};
    for (const e of this.relayHoldEntries) {
      const pwm = Number(e.pwm);
      if (Number.isFinite(pwm) && pwm > 0) obj[String(e.output)] = Math.round(pwm);
    }
    this.settings.relayHold = obj;
  }

  private parseHwParamValue(val: string): any {
    if (val === null || val === undefined) return val;
    const s = String(val).trim();
    if (s === '') return '';
    // boolean
    if (s.toLowerCase() === 'true') return true;
    if (s.toLowerCase() === 'false') return false;
    // number
    if (!isNaN(Number(s))) {
      // decide int vs float
      return s.indexOf('.') === -1 ? parseInt(s, 10) : parseFloat(s);
    }
    // JSON object/array
    if ((s.startsWith('{') && s.endsWith('}')) || (s.startsWith('[') && s.endsWith(']'))) {
      try {
        return JSON.parse(s);
      } catch {
        return s;
      }
    }
    return s;
  }
  
  /**
   * Strips surrounding whitespace from every string in the settings tree. Values are
   * routinely pasted in (SSID, cloud/OTA/NTP addresses), and a stray leading or trailing
   * space is invisible in the form but breaks the controller. Mutates in place so the
   * cleaned value is what the user sees if validation then rejects the form.
   */
  private trimStrings(obj: any): void {
    if (!obj || typeof obj !== 'object') return;
    for (const key of Object.keys(obj)) {
      const val = obj[key];
      if (typeof val === 'string') obj[key] = val.trim();
      else if (typeof val === 'object') this.trimStrings(val);
    }
  }

  validate():boolean {
    this.formError = '';
    this.formErrorParams = {};
    if (!this.settings) {
      this.formError = 'settings.err.not_loaded';
      return false;
    }
    // net
    // if (!this.settings?.ntp?.address) {
    //   this.formError = 'settings.err.required';
    //   this.formErrorParams = { field: 'NTP Server' };
    //   return false;
    // }
    // if (!this.settings?.ntp?.tz) {
    //   this.formError = 'settings.err.required';
    //   this.formErrorParams = { field: 'NTP Server timezone' };
    //   return false;
    // }
    if (this.settings?.cloud?.enabled && !this.settings?.cloud.address) {
      this.formError = 'settings.err.required';
      this.formErrorParams = { field: 'Cloud address' };
      return false;
    }
    if (this.settings?.eth?.enabled) {
      if (this.settings?.eth.enableReset && this.settings?.eth.resetGpio == null) {
        this.formError = 'settings.err.required';
        this.formErrorParams = { field: 'Ethernet resetGPIO' };
        return false;
      }
      if (!this.settings?.eth.dhcp && !this.settings?.eth.ip) {
        this.formError = 'settings.err.required';
        this.formErrorParams = { field: 'Ethernet IP' };
        return false;
      }
      if (!this.settings?.eth.dhcp && !this.settings?.eth.mask) {
        this.formError = 'settings.err.required';
        this.formErrorParams = { field: 'Ethernet Netmask' };
        return false;
      }
      if (!this.settings?.eth.dhcp && !this.settings?.eth.gw) {
        this.formError = 'settings.err.required';
        this.formErrorParams = { field: 'Ethernet Gateway' };
        return false;
      }
      if (!this.settings?.eth.dhcp && !this.settings?.eth.dns) {
        this.formError = 'settings.err.required';
        this.formErrorParams = { field: 'Ethernet DNS' };
        return false;
      }
    }
    if (this.settings?.wifi?.enabled) {
      if (!this.settings?.wifi.ssid) {
        this.formError = 'settings.err.required';
        this.formErrorParams = { field: 'WiFi SSID' };
        return false;
      }
      if (!this.settings?.wifi.pass) {
        this.formError = 'settings.err.required';
        this.formErrorParams = { field: 'WiFi Password' };
        return false;
      }
      if (!this.settings?.wifi.dhcp && !this.settings?.wifi.ip) {
        this.formError = 'settings.err.required';
        this.formErrorParams = { field: 'Wifi IP' };
        return false;
      }
      if (!this.settings?.wifi.dhcp && !this.settings?.wifi.mask) {
        this.formError = 'settings.err.required';
        this.formErrorParams = { field: 'Wifi Netmask' };
        return false;
      }
      if (!this.settings?.wifi.dhcp && !this.settings?.wifi.gw) {
        this.formError = 'settings.err.required';
        this.formErrorParams = { field: 'Wifi Gateway' };
        return false;
      }
      if (!this.settings?.wifi.dhcp && !this.settings?.wifi.dns) {
        this.formError = 'settings.err.required';
        this.formErrorParams = { field: 'Wifi DNS' };
        return false;
      }
    }
    return true;
  }

  saveSettings() {
    this.trimStrings(this.settings);
    if (!this.validate()) {
      return;
    }
    //console.log('save',this.settings);
    // serialize hwParams into config.hwParams before sending
    this.serializeRelayHold();
    this.websocketService.sendMessage({
      type: 'UPDATESETTINGS',
      payload: { mac: this.mac, settings: this.settings },
    });    
    this.back();
  }

  public back(): void {
    this.animationService.triggerLeaveAnimation();
    setTimeout(() => {
      if (this.mac) {
        this.router.navigate(['/controller', this.mac]);
      } else {
        this.location.back();
      }
    }, 150); // Half the animation duration for smoother transition
    // <!-- <button class="back-btn" [routerLink]="['/controller', mac]">Back</button> -->
  }

}
